import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Button, Loader, Modal } from "semantic-ui-react";
import styled from "styled-components";
import { t } from "../../../constant/translations";
import { reverse, routes } from "../../../routes";
import { device } from "../../../config";
import { BackButton } from "../../common/back-button";
import { fetchSurvey, deleteSurvey } from "../../../redux/modules/survey";
import { fetchSurveyVotes } from "../../../redux/modules/survey-vote";

const PageWrap = styled.div`
  padding: 16px 20px;

  @media ${device.desktop} {
    padding: 20px 28px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
`;

const PageTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: #1c1917;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 16px;

  @media ${device.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  background: #fff;
  padding: 12px 14px;
  text-align: center;
`;

const StatVal = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ color }) => color || "#1c1917"};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #78716c;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e7e5e4;
  margin-bottom: 16px;
`;

const Tab = styled.div`
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
  border-bottom: 2px solid
    ${({ active }) => (active ? "#1a7fc1" : "transparent")};
  color: ${({ active }) => (active ? "#1a7fc1" : "#78716c")};
  font-weight: ${({ active }) => (active ? 600 : 400)};
  margin-bottom: -1px;
`;

const QuestionCard = styled.div`
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 12px;
`;

const QuestionCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #e7e5e4;
  background: #f8f9fb;
`;

const QuestionCardTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1c1917;
`;

const AcceptedBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 8px;
  background: #d1fae5;
  color: #065f46;
`;

const QuestionCardBody = styled.div`
  padding: 12px 14px;
`;

const WinnerBox = styled.div`
  font-size: 11px;
  color: #555;
  background: #f0fdf4;
  border: 1px solid #c3e6cb;
  border-radius: 3px;
  padding: 6px 10px;
  margin-bottom: 10px;
`;

const ResultRow = styled.div`
  margin-bottom: 10px;
`;

const ResultLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 4px;
`;

const ResultBarOuter = styled.div`
  height: 6px;
  background: #f0f0ef;
  border-radius: 3px;
  overflow: hidden;
`;

const BAR_COLORS = ["#1a7fc1", "#dc3545", "#ffc107", "#6c757d"];

const ResultBarInner = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${({ colorIndex }) =>
    BAR_COLORS[Math.min(colorIndex, BAR_COLORS.length - 1)]};
  width: ${({ pct }) => pct}%;
`;

const VotersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    text-align: left;
    padding: 6px 10px;
    border-bottom: 1px solid #e7e5e4;
    font-size: 11px;
    font-weight: 600;
    color: #a8a29e;
  }

  td {
    padding: 7px 10px;
    border-bottom: 1px solid #f0f0ef;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const VoterStatusBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  border-radius: 3px;
  padding: 1px 6px;
  ${({ voted }) =>
    voted
      ? "background: #d4edda; color: #155724;"
      : "background: #f8d7da; color: #721c24;"}
`;

const ProtocolSection = styled.div`
  margin-top: 24px;
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  overflow: hidden;
`;

const ProtocolHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f8f9fb;
  border-bottom: 1px solid #e7e5e4;
  font-size: 13px;
  font-weight: 600;
`;

const ProtocolBody = styled.div`
  padding: 20px;
  background: #fff;
  font-size: 12px;
  line-height: 1.7;
`;

const ProtocolTitle = styled.h2`
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const ProtocolSubtitle = styled.p`
  text-align: center;
  font-size: 11px;
  color: #555;
  margin-bottom: 16px;
`;

const ProtocolSectionLabel = styled.div`
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const ProtoTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-top: 8px;

  th {
    border: 1px solid #ddd;
    padding: 4px 8px;
    background: #f8f9fb;
    font-weight: 600;
    text-align: left;
  }

  td {
    border: 1px solid #ddd;
    padding: 4px 8px;
  }
`;

const ProtocolSigRow = styled.div`
  display: flex;
  gap: 40px;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid #eee;
`;

const SigBlock = styled.div`
  flex: 1;
  font-size: 12px;
`;

const SigLine = styled.div`
  border-bottom: 1px solid #333;
  margin-top: 24px;
  padding-bottom: 4px;
  color: #333;
`;

const Muted = styled.td`
  color: #78716c;
`;

// Tallies vote answers for a single question across all votes.
// For yesno questions the three option strings must match the values stored in
// SurveyVote.answers (set by the vote form) — do not translate them.
function computeQuestionResults(question, votes) {
  const options =
    question.qtype === "yesno"
      ? ["За", "Против", "Въздържал се"]
      : question.options || [];

  const tally = {};
  options.forEach((o) => (tally[o] = 0));

  votes.forEach((vote) => {
    const answer = vote.answers[String(question.id)];
    if (answer !== undefined && answer !== null) {
      tally[answer] = (tally[answer] || 0) + 1;
    }
  });

  const total = Object.values(tally).reduce((s, v) => s + v, 0);
  const results = options.map((o) => ({
    l: o,
    v: tally[o] || 0,
    p: total > 0 ? Math.round(((tally[o] || 0) / total) * 100) : 0,
  }));

  let winner = null;
  if (question.qtype === "choose" && results.length > 0) {
    winner = results.reduce((a, b) => (b.v > a.v ? b : a)).l;
  }

  const accepted = question.qtype === "choose" ? total > 0 : results[0]?.p > 50;

  return { results, winner, accepted };
}

function SurveyResultsComponent({
  match,
  history,
  survey,
  votes,
  loading,
  isManager,
  fetchSurvey,
  fetchSurveyVotes,
  deleteSurvey,
}) {
  const blockId = parseInt(match.params.blockId);
  const surveyId = parseInt(match.params.surveyId);
  const [tab, setTab] = useState("results");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSurvey(surveyId);
    fetchSurveyVotes(surveyId);
  }, [surveyId]);

  const handleDelete = () => {
    setDeleting(true);
    deleteSurvey(surveyId).then(() => {
      history.push(reverse(routes.blockDetails, { blockId }));
    });
  };

  if (loading || !survey)
    return <Loader active inline="centered" size="small" />;

  const questions = survey.questions || [];
  const voteCount = votes.length;
  const votedWeightPct = parseFloat(survey.voted_weight_pct || 0);

  const protocolUrl = `/api/survey/${surveyId}/protocol.docx`;

  return (
    <PageWrap>
      <PageHeader>
        <BackButton size="tiny" />
        <PageTitle>{survey.title}</PageTitle>
        {isManager && survey.status === "closed" && (
          <HeaderActions>
            <Button size="mini" primary as="a" href={protocolUrl} download>
              {t("survey.download_pdf")}
            </Button>
            <Button
              size="mini"
              basic
              negative
              onClick={() => setDeleteOpen(true)}
            >
              {t("labels.delete")}
            </Button>
          </HeaderActions>
        )}
      </PageHeader>

      <StatsRow>
        <StatCard>
          <StatVal>{voteCount}</StatVal>
          <StatLabel>{t("survey.voted_estates")}</StatLabel>
        </StatCard>
        <StatCard>
          <StatVal color="#28a745">{votedWeightPct.toFixed(1)}%</StatVal>
          <StatLabel>{t("survey.ideal_parts")}</StatLabel>
        </StatCard>
      </StatsRow>

      <Tabs>
        <Tab active={tab === "results"} onClick={() => setTab("results")}>
          {t("survey.results")}
        </Tab>
        <Tab active={tab === "voters"} onClick={() => setTab("voters")}>
          {t("survey.voters")}
        </Tab>
      </Tabs>

      {tab === "results" && (
        <div>
          {questions.map((q, qi) => {
            const { results, winner, accepted } = computeQuestionResults(
              q,
              votes
            );
            return (
              <QuestionCard key={q.id}>
                <QuestionCardHeader>
                  <QuestionCardTitle>
                    {t("survey.question_num_short")} {qi + 1} &middot; {q.title}
                  </QuestionCardTitle>
                  {accepted ? (
                    <AcceptedBadge>{t("survey.accepted")}</AcceptedBadge>
                  ) : (
                    <AcceptedBadge
                      style={{ background: "#fce8e8", color: "#7f1d1d" }}
                    >
                      {t("survey.rejected")}
                    </AcceptedBadge>
                  )}
                </QuestionCardHeader>
                <QuestionCardBody>
                  {q.qtype === "choose" && winner && (
                    <WinnerBox>
                      {t("survey.winner")}: <strong>{winner}</strong> —{" "}
                      {t("survey.winner_note_result")}
                    </WinnerBox>
                  )}
                  {results.map((r, i) => (
                    <ResultRow key={r.l}>
                      <ResultLabel>
                        <span style={{ fontWeight: i === 0 ? 600 : 400 }}>
                          {r.l}
                        </span>
                        <span style={{ color: "#777" }}>
                          {r.v} {t("survey.result_votes")} &middot; {r.p}%
                        </span>
                      </ResultLabel>
                      <ResultBarOuter>
                        <ResultBarInner pct={r.p} colorIndex={i} />
                      </ResultBarOuter>
                    </ResultRow>
                  ))}
                </QuestionCardBody>
              </QuestionCard>
            );
          })}
        </div>
      )}

      {tab === "voters" && (
        <QuestionCard>
          <QuestionCardHeader>
            <QuestionCardTitle>{t("survey.voters")}</QuestionCardTitle>
            <span style={{ fontSize: 11, color: "#78716c" }}>
              {voteCount} {t("survey.voted_count")}
            </span>
          </QuestionCardHeader>
          <div style={{ overflowX: "auto" }}>
            <VotersTable>
              <thead>
                <tr>
                  <th>{t("survey.owner_col")}</th>
                  <th>{t("survey.estate_col")}</th>
                  <th>{t("labels.date")}</th>
                </tr>
              </thead>
              <tbody>
                {votes.map((v) => (
                  <tr key={v.id}>
                    <td style={{ fontWeight: 500 }}>
                      {v.voter_name || v.voter}
                    </td>
                    <Muted>{v.estate_name || v.estate}</Muted>
                    <Muted>
                      {v.created_at
                        ? new Date(v.created_at).toLocaleDateString("bg-BG")
                        : "—"}
                    </Muted>
                  </tr>
                ))}
              </tbody>
            </VotersTable>
          </div>
        </QuestionCard>
      )}
      <Modal
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        size="tiny"
      >
        <Modal.Header>{t("labels.delete")}</Modal.Header>
        <Modal.Content>
          <p>{t("survey.delete_confirm")}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            basic
            onClick={() => setDeleteOpen(false)}
            disabled={deleting}
          >
            {t("common.back")}
          </Button>
          <Button
            negative
            loading={deleting}
            disabled={deleting}
            onClick={handleDelete}
          >
            {t("labels.delete")}
          </Button>
        </Modal.Actions>
      </Modal>
    </PageWrap>
  );
}

const mapStateToProps = (state, ownProps) => {
  const surveyId = parseInt(ownProps.match?.params?.surveyId);
  return {
    survey: (state.survey.entities || []).find((s) => s.id === surveyId),
    votes: (state.surveyVote.entities || []).filter(
      (v) => v.survey === surveyId
    ),
    loading: state.survey.loading,
    isManager: state.auth.user?.is_manager,
  };
};

export const SurveyResults = withRouter(
  connect(mapStateToProps, { fetchSurvey, fetchSurveyVotes, deleteSurvey })(
    SurveyResultsComponent
  )
);
